import { MapPin, Calendar, Clock, Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
// Supabase client lazy-loaded on submit to keep landing-page JS minimal.
import type { LorsCopy } from "./translations";
import { getUtms } from "./utmStorage";

const DEFAULT_DIRECTIONS_URL =
  "https://www.google.com/maps/dir//Finley+Community+Center:+Santa+Rosa+Recreation+%26+Parks,+2060+W+College+Ave,+Santa+Rosa,+CA+95401/@38.4441709,-122.7517363,17z/data=!4m17!1m7!3m6!1s0x808438384efc937b:0x54cb20ecdd94d3b9!2sFinley+Community+Center:+Santa+Rosa+Recreation+%26+Parks!8m2!3d38.4441667!4d-122.7491667!16s%2Fg%2F1q6jb36pk!4m8!1m0!1m5!1m1!1s0x808438384efc937b:0x54cb20ecdd94d3b9!2m2!1d-122.7492476!2d38.4442332!3e0?entry=ttu&g_ep=EgoyMDI2MDQyMS4wIKXMDSoASAFQAw%3D%3D";

const LorsForm = ({
  copy,
  redirectTo,
  directionsUrl = DEFAULT_DIRECTIONS_URL,
  formKey,
  hideMeta = false,
}: {
  copy: LorsCopy;
  redirectTo: string;
  directionsUrl?: string;
  formKey: "may13" | "may20" | "may29" | "june2" | "june25" | "july16" | "july24" | "aug4" | "aug12" | "sep10" | "sep22" | "sep23" | "oct15" | "oct22" | "oct28" | "nov10" | "nov19" | "dec9" | "dec17" | "odw";
  hideMeta?: boolean;
}) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [smsConsent, setSmsConsent] = useState(false);
  const [enewsConsent, setEnewsConsent] = useState(false);

  const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const formData = new FormData(e.currentTarget);
    const raw = {
      first_name: String(formData.get("first_name") ?? "").trim().slice(0, 100),
      last_name: String(formData.get("last_name") ?? "").trim().slice(0, 100),
      email: String(formData.get("email") ?? "").trim().slice(0, 255),
      phone: String(formData.get("phone") ?? "").trim().slice(0, 30),
      sms_consent: smsConsent,
      enews_consent: enewsConsent,
    };

    const fieldErrors: Record<string, string> = {};
    if (!raw.first_name) fieldErrors.first_name = copy.validation.firstName;
    if (!raw.last_name) fieldErrors.last_name = copy.validation.lastName;
    if (!raw.email || !EMAIL_RE.test(raw.email)) fieldErrors.email = copy.validation.email;
    if (raw.phone.length < 7) fieldErrors.phone = copy.validation.phone;
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    const parsed = { data: raw };

    setSubmitting(true);
    try {
      const utms = getUtms();
      try {
        sessionStorage.setItem(`lors_utms_${formKey}`, JSON.stringify(utms));
        sessionStorage.setItem(
          `lors_primary_${formKey}`,
          JSON.stringify({
            first_name: parsed.data.first_name,
            last_name: parsed.data.last_name,
            email: parsed.data.email,
            phone: parsed.data.phone,
          }),
        );
      } catch { /* ignore */ }

      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("lawmatics-graphql-intake", {
        body: { ...parsed.data, utms, form_key: formKey },
      });
      if (error) {
        let errBody: { code?: string; error?: string } | null = null;
        try {
          errBody = await (error as { context?: Response }).context?.json();
        } catch { /* ignore */ }
        if (errBody?.code === "invalid_email" || errBody?.error === "valid email is required") {
          setErrors({ email: copy.validation.email });
          setSubmitting(false);
          return;
        }
        throw error;
      }
      if (data && (data as { ok?: boolean }).ok === false) {
        throw new Error((data as { error?: string }).error ?? "Submission failed");
      }
      navigate(redirectTo);
    } catch (err) {
      console.error("Lawmatics submission failed:", err);
      toast.error(copy.errorTitle, { description: copy.errorBody });
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-xl p-8 md:p-10 shadow-lg border border-border">
      {!hideMeta && (
        <>
          <div className="flex flex-col items-center gap-2 mb-6 text-base sm:text-sm">
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" style={{ color: "hsl(210 24% 20%)" }} />
                <span>
                  <span className="sm:hidden">{copy.formDate.short}</span>
                  <span className="hidden sm:inline">{copy.formDate.long}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 sm:block hidden" style={{ color: "hsl(210 24% 20%)" }} />
                <span>{copy.formTime}</span>
              </div>
            </div>
            <div className="flex items-start gap-2 text-center">
              <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "hsl(210 24% 20%)" }} />
              <span>
                {copy.formVenueLine1}
                <br />
                {copy.formVenueLine2}
                {copy.getDirections && (
                  <>
                    <br />
                    <a
                      href={directionsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs underline mt-1 inline-block"
                      style={{ color: "hsl(210 24% 35%)" }}
                    >
                      {copy.getDirections} →
                    </a>
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="border-t border-border mb-6" />
        </>
      )}


      <h2 className="lors-headline text-2xl mb-2 text-center">{copy.formTitle}</h2>
      <p className="text-sm text-center mb-6" style={{ color: "hsl(210 24% 35%)" }}>
        {copy.formSubtitle}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="first_name" className="sr-only">{copy.formFirstName}</Label>
            <Input
              id="first_name"
              name="first_name"
              placeholder={copy.formFirstName}
              autoComplete="given-name"
              required
              maxLength={100}
              aria-invalid={!!errors.first_name}
            />
            {errors.first_name && (
              <p className="text-xs text-destructive">{errors.first_name}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="last_name" className="sr-only">{copy.formLastName}</Label>
            <Input
              id="last_name"
              name="last_name"
              placeholder={copy.formLastName}
              autoComplete="family-name"
              required
              maxLength={100}
              aria-invalid={!!errors.last_name}
            />
            {errors.last_name && (
              <p className="text-xs text-destructive">{errors.last_name}</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="sr-only">{copy.formEmail}</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder={copy.formEmail}
            inputMode="email"
            autoComplete="email"
            required
            maxLength={255}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phone" className="sr-only">{copy.formPhone}</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder={copy.formPhone}
            inputMode="tel"
            autoComplete="tel"
            required
            maxLength={30}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && (
            <p className="text-xs text-destructive">{errors.phone}</p>
          )}
        </div>

        <div className="flex items-start gap-3 pt-1">
          <Checkbox
            id="sms_consent"
            checked={smsConsent}
            onCheckedChange={(v) => setSmsConsent(v === true)}
            className="mt-0.5"
          />
          <Label
            htmlFor="sms_consent"
            className="text-xs leading-tight font-normal cursor-pointer"
            style={{ color: "hsl(210 24% 35%)" }}
          >
            {copy.formSmsConsent}
          </Label>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            id="enews_consent"
            checked={enewsConsent}
            onCheckedChange={(v) => setEnewsConsent(v === true)}
            className="mt-0.5"
          />
          <Label
            htmlFor="enews_consent"
            className="text-xs leading-tight font-normal cursor-pointer"
            style={{ color: "hsl(210 24% 35%)" }}
          >
            {copy.formEnewsConsent}
          </Label>
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full font-semibold text-base"
          style={{
            backgroundColor: "hsl(34 23% 72%)",
            color: "hsl(210 24% 20%)",
            fontFamily: "'Montserrat', system-ui, sans-serif",
          }}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {copy.formSubmitting}
            </>
          ) : (
            copy.formSubmit
          )}
        </Button>
      </form>

      {copy.formTrust && (
        <p className="text-sm text-center mt-4" style={{ color: "hsl(210 24% 35%)" }}>
          {copy.formTrust.split("|").map((part, i, arr) => (
            <span key={i}>
              {part.trim()}
              {i < arr.length - 1 && (
                <>
                  <span className="hidden sm:inline"> | </span>
                  <br className="sm:hidden" />
                </>
              )}
            </span>
          ))}
        </p>
      )}
    </div>
  );
};

export default LorsForm;
